// src/pages/appointments/past/PastAppointmentDetails.jsx
import React from "react";
import Card from "@/components/ui/Card.jsx";
import { useNavigate, useParams } from "react-router-dom";

import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import Snackbar from "@/sot/primitives/Snackbar.jsx";
import DoctorResultCard from "@/components/doctor/DoctorResultCard.jsx";
import Button from "@/components/ui/Button.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import AppointmentDeleteDialog from "@/pages/appointments/details/AppointmentDeleteDialog.jsx";
import { loadPatientAppointmentSnapshot } from "@/services/patientAppointmentSnapshots.js";
import {
  listAppointmentChangeEvents,
  summarizeLatestAppliedReschedule,
} from "@/services/appointmentChangeEvents.js";
import { resolveDoctorBookingEntryState } from "@/services/doctorBookingMode.js";
import {
  loadMyAppointmentDoctorReview,
  saveMyAppointmentDoctorReview,
} from "@/services/appointmentDoctorReviews.js";
import { getCurrentPatientId } from "@/services/appointmentService.js";
import { buildFavoriteDoctorIds } from "@/services/doctorFavorites.js";
import { supabase } from "@/services/supabaseClient";
import { pickDiagnosisNotes, pickDiagnosisTitle } from "@/utils/diagnosis.js";

const ICON_CARD = "/icons/appointments/credit_card.svg";
const ICON_HOSPITAL = "/icons/appointments/hospital_Fill.svg";
const ICON_VIDEO = "/icons/appointments/afrimed-EVisit.svg";

function RatingStarIcon({ active = false }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={`h-6 w-6 ${active ? "text-[rgb(var(--afrimed-secondary))]" : "text-[rgba(var(--clr-on-surface-variant),0.68)]"}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 2.9L14.76 8.49L20.93 9.39L16.46 13.74L17.51 19.89L12 16.99L6.49 19.89L7.54 13.74L3.07 9.39L9.24 8.49L12 2.9Z"
        fill={active ? "currentColor" : "none"}
        stroke={active ? "none" : "currentColor"}
        strokeWidth={active ? 0 : 1.9}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RatingSelector({ label, value, onChange, hint }) {
  return (
    <div>
      <div className="text-title-small text-on-surface">{label}</div>
      <div className="mt-2 flex items-center gap-2">
        {Array.from({ length: 5 }, (_, index) => {
          const starValue = index + 1;
          const isActive = starValue <= Number(value || 0);
          return (
            <button
              key={starValue}
              type="button"
              aria-label={`${label}: ${starValue} of 5`}
              aria-pressed={isActive}
              onClick={() => onChange(starValue)}
              className={`
                h-11 w-11 rounded-[12px] inline-flex items-center justify-center transition
                ${isActive
                  ? "bg-[rgba(var(--afrimed-secondary),0.16)]"
                  : "bg-[rgba(15,23,42,0.04)]"}
              `}
            >
              <RatingStarIcon active={isActive} />
            </button>
          );
        })}
      </div>
      {hint ? <div className="mt-2 text-body-small text-on-surface-var">{hint}</div> : null}
    </div>
  );
}

function formatFullDate(dateISO, formatDate, t) {
  const d = new Date(`${dateISO}T00:00:00`);
  if (Number.isNaN(d.getTime())) return t("appointments.past.common.unknownDate", "—");
  return formatDate(d, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function localizePaymentMethodLabel(
  value,
  t = (_, fallback) => fallback,
  fallback = "",
) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return fallback || t("appointments.past.details.paymentFallback", "Debit / Credit cards");

  if (
    raw === "card" ||
    raw === "credit" ||
    raw === "debit" ||
    raw.includes("card") ||
    raw.includes("visa") ||
    raw.includes("mastercard") ||
    raw.includes("amex")
  ) {
    return t("appointments.past.details.paymentFallback", "Debit / Credit cards");
  }
  if (raw === "bank" || raw.includes("bank") || raw.includes("transfer")) {
    return t("appointments.confirm.payment.bank", "Bank transfers");
  }
  if (raw === "cash" || raw.includes("cash")) {
    return t("appointments.confirm.payment.cash", "Cash on service");
  }
  if (raw === "eft" || raw.includes("eft")) {
    return t("appointments.confirm.payment.eft", "Instant EFT");
  }
  if (raw === "wallet" || raw.includes("wallet")) {
    return t("appointments.confirm.payment.wallet", "E-wallets");
  }
  if (raw.includes("pending")) {
    return t("appointments.past.details.paymentPending", "Payment pending");
  }

  return fallback || value;
}

export default function PastAppointmentDetails() {
  const nav = useNavigate();
  const { t, formatDate, formatDateTime, languageCode } = useLanguage();
  const params = useParams();
  const id = params.id || params.appointmentId || params.pastId || "";

  const [appt, setAppt] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [latestPrescription, setLatestPrescription] = React.useState(null);
  const [changeEvents, setChangeEvents] = React.useState([]);
  const [review, setReview] = React.useState(null);
  const [reviewLoading, setReviewLoading] = React.useState(true);
  const [reviewSaving, setReviewSaving] = React.useState(false);
  const [reviewError, setReviewError] = React.useState("");
  const [reviewToast, setReviewToast] = React.useState("");
  const [reviewDraft, setReviewDraft] = React.useState({
    doctorRating: 0,
    experienceRating: 0,
    reviewText: "",
  });

  React.useEffect(() => {
    let alive = true;

    async function load() {
      if (!id) {
        if (alive) {
          setAppt(null);
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      setError("");
      setReviewLoading(true);
      setReviewError("");
      try {
        const [snapshotRes, changeEventsRes, prescriptionRes, reviewRes] = await Promise.allSettled([
          loadPatientAppointmentSnapshot(id),
          listAppointmentChangeEvents(id),
          supabase
            .from("prescriptions")
            .select("id, diagnosis, notes, meta, issued_at, created_at")
            .eq("appointment_id", id)
            .order("issued_at", { ascending: false, nullsFirst: false })
            .order("created_at", { ascending: false })
            .limit(1)
            .maybeSingle(),
          loadMyAppointmentDoctorReview(id),
        ]);
        if (!alive) return;

        if (snapshotRes.status !== "fulfilled") {
          throw snapshotRes.reason;
        }

        setAppt(snapshotRes.value);
        setChangeEvents(
          changeEventsRes.status === "fulfilled" && Array.isArray(changeEventsRes.value)
            ? changeEventsRes.value
            : []
        );
        setLatestPrescription(
          prescriptionRes.status === "fulfilled" ? prescriptionRes.value?.data || null : null
        );
        if (reviewRes.status === "fulfilled") {
          setReview(reviewRes.value);
        } else {
          setReview(null);
          setReviewError(
            reviewRes.reason?.message ||
              t(
                "appointments.past.review.errors.load",
                "Unable to load your doctor review for this appointment."
              )
          );
        }
        setReviewDraft({
          doctorRating:
            reviewRes.status === "fulfilled" ? Number(reviewRes.value?.doctorRating || 0) : 0,
          experienceRating:
            reviewRes.status === "fulfilled"
              ? Number(reviewRes.value?.experienceRating || 0)
              : 0,
          reviewText:
            reviewRes.status === "fulfilled" ? String(reviewRes.value?.reviewText || "") : "",
        });
      } catch (e) {
        if (!alive) return;
        setAppt(null);
        setChangeEvents([]);
        setLatestPrescription(null);
        setError(
          e?.message ||
            t("appointments.past.details.errors.load", "Unable to load past appointment.")
        );
        setReview(null);
        setReviewDraft({
          doctorRating: 0,
          experienceRating: 0,
          reviewText: "",
        });
        setReviewError("");
      } finally {
        if (alive) {
          setLoading(false);
          setReviewLoading(false);
        }
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [id]);

  const doctorForCard = (() => {
    const doctorBase = appt?.doctor || null;
    if (!doctorBase) return null;

    const favoriteIds = buildFavoriteDoctorIds(doctorBase, {
      doctor_id: appt?.doctor_id,
      doctorId: appt?.doctorId,
    });

    return {
      ...doctorBase,
      id: doctorBase?.id || favoriteIds[0] || null,
      doctor_id: doctorBase?.doctor_id || appt?.doctor_id || appt?.doctorId || null,
      favoriteIds,
    };
  })();

  if (loading || !appt) {
    return (
        <div data-hide-tabbar="true" className="min-h-[100dvh] bg-[rgb(var(--clr-surface-container-low))]">
        <div className="afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
          <TopBarCenter
            title={t("appointments.past.details.title", "Past appointments")}
            onBack={() => nav(-1)}
          />
        <Card className="mt-6 px-4 py-4 text-body-medium text-on-surface-var">
            {loading
              ? t("appointments.past.details.loading", "Loading past appointment...")
              : error || t("appointments.past.details.notFound", "Past appointment not found.")}
          </Card>
        </div>
      </div>
    );
  }
  const isInPerson = appt.mode === "inperson";
  const facilityAddress =
    appt?.facility?.address ||
    [appt?.facility?.city, appt?.facility?.country].filter(Boolean).join(", ");
  const rxMeta =
    latestPrescription?.meta && typeof latestPrescription.meta === "object"
      ? latestPrescription.meta
      : {};
  const diagnosisTitle = pickDiagnosisTitle(
    appt?.diagnosisTitle,
    appt?.diagnosis,
  );
  const diagnosisNotes = diagnosisTitle
    ? pickDiagnosisNotes(
        appt?.diagnosisNotes,
      )
    : "";
  const appointmentStatus = String(appt?.status || "").trim().toLowerCase();
  const appointmentStatusReason = String(appt?.appointmentStatusReason || "")
    .trim()
    .toLowerCase();
  const rescheduleSummary = summarizeLatestAppliedReschedule(changeEvents);
  const rescheduleHistoryText =
    rescheduleSummary?.oldAppointmentDate &&
    rescheduleSummary?.newAppointmentDate &&
    rescheduleSummary.oldAppointmentDate !== rescheduleSummary.newAppointmentDate
      ? t(
          "appointments.past.details.status.rescheduleHistory",
          "Originally booked for {oldDate}. Rescheduled to {newDate}.",
          {
            oldDate: formatDateTime(rescheduleSummary.oldAppointmentDate),
            newDate: formatDateTime(rescheduleSummary.newAppointmentDate),
          }
        )
      : "";
  const description =
    (diagnosisTitle
      ? [diagnosisTitle, diagnosisNotes].filter(Boolean).join(" — ")
      : "") ||
    (appointmentStatus === "missed"
      ? t(
          "appointments.past.details.descriptionFallbackMissed",
          "This appointment was missed. No diagnosis or notes were added."
        )
      : "") ||
    t(
      "appointments.past.details.descriptionFallback",
      "No diagnosis was recorded for this appointment."
    );
  const appointmentStatusLabel =
    appointmentStatus === "completed"
      ? t("appointments.past.details.status.completed", "Completed")
      : appointmentStatus === "missed"
        ? t("appointments.past.details.status.missed", "Missed")
        : appointmentStatus === "cancelled" && appointmentStatusReason === "rescheduled"
          ? t("appointments.past.details.status.rescheduled", "Rescheduled")
        : appointmentStatus === "cancelled" && appointmentStatusReason === "declined"
          ? t("appointments.past.details.status.declined", "Declined")
          : appointmentStatus === "cancelled"
            ? t("appointments.past.details.status.cancelled", "Cancelled")
            : appointmentStatus === "confirmed"
              ? t("appointments.past.details.status.confirmed", "Confirmed")
              : appointmentStatus === "scheduled"
                ? t("appointments.past.details.status.scheduled", "Scheduled")
                : appointmentStatus === "pending"
                  ? t("appointments.past.details.status.pending", "Pending")
                  : t("appointments.past.details.status.past", "Past");
  const appointmentStatusHelper =
    appointmentStatus === "completed"
      ? t(
          "appointments.past.details.status.completedHelp",
          "This consultation was completed."
        )
      : appointmentStatus === "missed"
        ? t(
            "appointments.past.details.status.missedHelp",
            "This appointment was missed. The video consultation window closed before the visit started."
          )
        : appointmentStatus === "cancelled" && appointmentStatusReason === "rescheduled"
          ? t(
              "appointments.past.details.status.rescheduledHelp",
              "This appointment slot was moved to a new approved time."
            )
        : appointmentStatus === "cancelled" && appointmentStatusReason === "declined"
          ? t(
              "appointments.past.details.status.declinedHelp",
              "This appointment was declined before the consultation took place."
            )
          : appointmentStatus === "cancelled"
            ? t(
                "appointments.past.details.status.cancelledHelp",
                "This appointment was cancelled before the consultation took place."
              )
            : t(
                "appointments.past.details.status.pendingCloseoutHelp",
                "This appointment is in the past but has not been marked completed yet."
              );
  const canReviewDoctor = appointmentStatus === "completed";
  const reviewUnavailableMessage =
    appointmentStatus === "missed"
      ? t(
          "appointments.past.review.unavailableMissed",
          "Doctor review is unavailable for a missed appointment."
        )
      : appointmentStatus === "cancelled" && appointmentStatusReason === "rescheduled"
        ? t(
            "appointments.past.review.unavailableRescheduled",
            "Doctor review is unavailable for a rescheduled slot."
          )
      : appointmentStatus === "cancelled"
        ? t(
            "appointments.past.review.unavailableCancelled",
            "Doctor review is unavailable for a cancelled appointment."
          )
        : t(
            "appointments.past.review.unavailablePendingCloseout",
            "Doctor review becomes available after the appointment is marked completed."
          );
  const hasExistingReview = Boolean(review?.id);
  const reviewDirty =
    Number(reviewDraft.doctorRating || 0) !== Number(review?.doctorRating || 0) ||
    Number(reviewDraft.experienceRating || 0) !== Number(review?.experienceRating || 0) ||
    String(reviewDraft.reviewText || "").trim() !== String(review?.reviewText || "").trim();
  const canSubmitReview =
    canReviewDoctor &&
    Number(reviewDraft.doctorRating || 0) >= 1 &&
    Number(reviewDraft.experienceRating || 0) >= 1 &&
    !reviewSaving &&
    reviewDirty;

  async function handleReviewSubmit() {
    if (!canSubmitReview) return;

    setReviewSaving(true);
    setReviewError("");
    try {
      const result = await saveMyAppointmentDoctorReview({
        appointmentId: id,
        doctorRating: reviewDraft.doctorRating,
        experienceRating: reviewDraft.experienceRating,
        reviewText: reviewDraft.reviewText,
        languageCode,
      });
      setReview(result?.review || null);
      setReviewDraft({
        doctorRating: Number(result?.review?.doctorRating || 0),
        experienceRating: Number(result?.review?.experienceRating || 0),
        reviewText: String(result?.review?.reviewText || ""),
      });
      if (result?.aggregate?.rating) {
        setAppt((current) =>
          current
            ? {
                ...current,
                doctor: {
                  ...(current.doctor || {}),
                  rating: result.aggregate.rating,
                },
              }
            : current
        );
      }
      setReviewToast(
        review?.id
          ? t("appointments.past.review.updated", "Your review was updated.")
          : t("appointments.past.review.saved", "Your review was submitted.")
      );
    } catch (submitError) {
      setReviewError(
        submitError?.message ||
          t(
            "appointments.past.review.errors.save",
            "We could not save your review right now."
          )
      );
    } finally {
      setReviewSaving(false);
    }
  }

  return (
    <div data-hide-tabbar="true" className="min-h-[100dvh] bg-[rgb(var(--clr-surface-container-low))]">
      <div className="afrimed-rail">
        {/* ✅ Blueprint title */}
        <TopBarCenter
          title={t("appointments.past.details.title", "Past appointments")}
          onBack={() => nav(-1)}
        />

        {/* ✅ Doctor card: use SSOT component */}
        <div className="mt-4">
          <DoctorResultCard
            doctor={doctorForCard}
            avatarSize={68}
            onToggleFavorite={() => {
              // hook later
            }}
          />
        </div>

        {/* ✅ Appointment type (in-person / virtual) + auto location */}
        <Card className="mt-4 overflow-hidden">
          <div className="px-4 py-4 flex items-center gap-3">
            {isInPerson ? (
              <img
                src={ICON_HOSPITAL}
                alt=""
                className="w-6 h-6 opacity-80"
                draggable="false"
              />
            ) : (
              <div className="w-6 shrink-0 flex items-center justify-center">
                <img
                  src={ICON_VIDEO}
                  alt=""
                  className="h-6 w-6 object-contain"
                  draggable="false"
                />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-label-medium text-on-surface-var">
                {isInPerson
                  ? t("appointments.past.details.mode.inPerson", "In-person")
                  : t("appointments.past.details.mode.virtual", "Virtual")}
              </div>

              {/* Only show location when in-person (as per your rule) */}
              {isInPerson ? (
                <div className="text-body-large text-on-surface -mt-0.5 truncate">
                  {appt.facilityName}
                </div>
              ) : (
                <div className="text-body-large text-on-surface -mt-0.5 truncate">
                  {t("appointments.past.details.videoConsultation", "Video consultation")}
                </div>
              )}
            </div>
          </div>

          {isInPerson ? (
            <>
              <div className="h-px w-full bg-[var(--Schemes-Outline-Variant,#E6E7EB)]" />
              <div className="px-4 pb-4 text-body-medium text-on-surface-var">
                {facilityAddress ||
                  t("appointments.past.details.locationFallback", "Consultation location")}
              </div>
            </>
          ) : null}
        </Card>

        {/* Appointment time */}
        <div className="mt-6">
          <div className="text-title-medium text-on-surface-var mb-3">
            {t("appointments.past.details.sections.time", "Appointment time")}
          </div>
          <Card className="overflow-hidden">
            <div className="px-4 py-4 flex items-center gap-3">
              <div className="h-9 px-6 rounded-[10px] bg-[var(--state-primary-12)] text-[rgb(var(--afrimed-primary))] text-label-large flex items-center">
                {appt.selectedTime}
              </div>
              <div className="text-body-large text-on-surface">
                {formatFullDate(appt.selectedDate, formatDate, t)}
              </div>
            </div>
            <div className="h-px w-full bg-[var(--Schemes-Outline-Variant,#E6E7EB)]" />
            <div className="px-4 py-4">
              <div className="text-label-medium text-on-surface-var">
                {t("appointments.past.details.sections.status", "Status")}
              </div>
              <div className="mt-1 text-title-medium text-on-surface">{appointmentStatusLabel}</div>
              <div className="mt-1 text-body-medium text-on-surface-var">
                {appointmentStatusHelper}
              </div>
              {rescheduleHistoryText ? (
                <div className="mt-2 text-body-small text-on-surface-var">
                  {rescheduleHistoryText}
                </div>
              ) : null}
            </div>
          </Card>
        </div>

        {/* Appointment description */}
        <div className="mt-6">
          <div className="text-title-medium text-on-surface-var mb-3">
            {t("appointments.past.details.sections.description", "Appointment description")}
          </div>
          <Card className="overflow-hidden">
            <div className="px-4 py-4 text-body-large text-on-surface">
              {description}
            </div>
          </Card>
        </div>

        {/* Payment method */}
        <div className="mt-6">
          <div className="text-title-medium text-on-surface-var mb-3">
            {t("appointments.past.details.sections.payment", "Payment method")}
          </div>
          <Card className="overflow-hidden">
            <div className="px-4 py-4 flex items-center gap-3">
              <img src={ICON_CARD} alt="" className="w-6 h-6 opacity-80" draggable="false" />
              <div className="text-body-large text-on-surface flex-1">
                {localizePaymentMethodLabel(
                  appt.paymentMethod ||
                    appt.payment_method ||
                    appt.paymentMethodLabel ||
                    appt.paymentLabel,
                  t,
                  t("appointments.past.details.paymentFallback", "Debit / Credit cards"),
                )}
              </div>
              {Number.isFinite(Number(appt.amountPaid)) ? (
                <div className="text-body-large text-on-surface-var">
                  {Number(appt.amountPaid).toFixed(2)} {appt.doctor?.currency || "BWP"}
                </div>
              ) : null}
            </div>
          </Card>
        </div>

        {canReviewDoctor ? (
          <div className="mt-6">
            <div className="text-title-medium text-on-surface-var mb-3">
              {t("appointments.past.review.section", "Doctor review")}
            </div>
            <Card className="overflow-hidden">
              <div className="px-4 py-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-title-medium text-on-surface">
                      {t(
                        "appointments.past.review.title",
                        "Rate your doctor and experience"
                      )}
                    </div>
                    <div className="mt-1 text-body-medium text-on-surface-var">
                      {t(
                        "appointments.past.review.subtitle",
                        "Your feedback helps other patients make informed decisions after completed appointments."
                      )}
                    </div>
                  </div>
                  {review?.id ? (
                    <div className="rounded-full bg-[var(--state-primary-12)] px-3 py-1 text-label-medium text-primary">
                      {t("appointments.past.review.submitted", "Review submitted")}
                    </div>
                  ) : null}
                </div>

                {review?.updatedAt ? (
                  <div className="mt-3 text-body-small text-on-surface-var">
                    {t("appointments.past.review.lastUpdated", "Last updated {date}", {
                      date: formatDateTime(review.updatedAt, {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      }),
                    })}
                  </div>
                ) : null}

                {reviewLoading ? (
                  <div className="mt-4 text-body-medium text-on-surface-var">
                    {t("appointments.past.review.loading", "Loading your review...")}
                  </div>
                ) : (
                  <div className="mt-4 space-y-5">
                    <RatingSelector
                      label={t(
                        "appointments.past.review.doctorPrompt",
                        "How would you rate your doctor?"
                      )}
                      value={reviewDraft.doctorRating}
                      onChange={(doctorRating) =>
                        setReviewDraft((current) => ({ ...current, doctorRating }))
                      }
                      hint={t(
                        "appointments.past.review.doctorHint",
                        "Think about clarity, care, and professionalism."
                      )}
                    />

                    <RatingSelector
                      label={t(
                        "appointments.past.review.experiencePrompt",
                        "How would you rate your overall experience?"
                      )}
                      value={reviewDraft.experienceRating}
                      onChange={(experienceRating) =>
                        setReviewDraft((current) => ({ ...current, experienceRating }))
                      }
                      hint={t(
                        "appointments.past.review.experienceHint",
                        "Think about booking, timing, and how smooth the visit felt."
                      )}
                    />

                    <div>
                      <label className="text-title-small text-on-surface" htmlFor="appointment-review-text">
                        {t(
                          "appointments.past.review.textPrompt",
                          "Tell us more about your experience"
                        )}
                      </label>
                      <textarea
                        id="appointment-review-text"
                        value={reviewDraft.reviewText}
                        onChange={(event) =>
                          setReviewDraft((current) => ({
                            ...current,
                            reviewText: event.target.value.slice(0, 1200),
                          }))
                        }
                        placeholder={t(
                          "appointments.past.review.textPlaceholder",
                          "Share what stood out about the consultation. This can stay brief."
                        )}
                        className="
                          mt-2 min-h-[132px] w-full resize-y rounded-[12px]
                          border border-[rgba(15,23,42,0.10)] bg-[rgb(var(--clr-surface-cont-highest))]
                          px-4 py-3 text-body-large text-on-surface outline-none
                          placeholder:text-on-surface-var
                          focus:border-[rgb(var(--afrimed-primary))] focus:ring-2 focus:ring-[var(--state-primary-12)]
                        "
                      />
                      <div className="mt-2 text-body-small text-on-surface-var">
                        {t(
                          "appointments.past.review.textHelper",
                          "{count}/1200 characters",
                          { count: String(reviewDraft.reviewText || "").length }
                        )}
                      </div>
                    </div>

                    {reviewError ? (
                      <div className="rounded-[12px] bg-[var(--state-critical-08)] px-4 py-3 text-body-medium text-[rgb(var(--schemes-critical))]">
                        {reviewError}
                      </div>
                    ) : null}

                    {!reviewDirty && hasExistingReview ? (
                      <div className="rounded-[12px] bg-[rgba(15,23,42,0.04)] px-4 py-3 text-body-medium text-on-surface-var">
                        {t(
                          "appointments.past.review.savedHint",
                          "Your review is saved. Change a rating or note if you want to update it."
                        )}
                      </div>
                    ) : null}

                    <div className="flex gap-3">
                      {reviewDirty ? (
                        <Button
                          variant="tonal"
                          className="flex-1 h-12"
                          disabled={reviewSaving}
                          onClick={() =>
                            setReviewDraft({
                              doctorRating: Number(review?.doctorRating || 0),
                              experienceRating: Number(review?.experienceRating || 0),
                              reviewText: String(review?.reviewText || ""),
                            })
                          }
                        >
                          {t("appointments.past.review.reset", "Reset")}
                        </Button>
                      ) : null}
                      <Button
                        className={`${reviewDirty ? "flex-1" : "w-full"} h-12`}
                        loading={reviewSaving}
                        disabled={!canSubmitReview}
                        onClick={handleReviewSubmit}
                      >
                        {hasExistingReview
                          ? reviewDirty
                            ? t("appointments.past.review.updateCta", "Update review")
                            : t("appointments.past.review.savedCta", "Review saved")
                          : t("appointments.past.review.submitCta", "Submit review")}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          </div>
        ) : (
          <div className="mt-6">
            <div className="text-title-medium text-on-surface-var mb-3">
              {t("appointments.past.review.section", "Doctor review")}
            </div>
            <Card className="overflow-hidden">
              <div className="px-4 py-4 text-body-medium text-on-surface-var">
                {reviewUnavailableMessage}
              </div>
            </Card>
          </div>
        )}

        {/* Bottom CTAs */}
        <div className="mt-8 flex gap-3">
          <Button
            variant="ghost"
            className="flex-1 h-12"
            onClick={() => {
            const doctorId = appt?.doctor?.id || "";
            if (!doctorId) return;

            // If in-person: let them confirm facility first
            if (appt?.mode === "inperson") {
                nav(`/appointments/edit-location/${appt.id}`, {
                state: {
                    __flow: "rebook",
                    fromPastAppointmentId: appt.id,
                    doctor: appt.doctor,
                    mode: "inperson",
                    facilityId: appt.facilityId || null,
                    facilityName: appt.facilityName || "",
                    facility: appt.facility || null,
                },
                });
                return;
            }

            // If e-visit: go straight to availability
            (async () => {
              const resolved = await resolveDoctorBookingEntryState({
                doctorId,
                doctor: appt.doctor || null,
                requestedMode: "evisit",
                consultationType: appt?.consultation_type || null,
                facilityId: appt?.facilityId || null,
                facilityName: appt?.facilityName || "",
                facility: appt?.facility || null,
              });

              nav(`/appointments/book/${doctorId}`, {
                state: {
                  doctor: appt.doctor,
                  mode: resolved.mode,
                  facilityId: resolved.facilityId,
                  facilityName: resolved.facilityName,
                  facility: resolved.facility,
                  __flow: "rebook",
                  fromPastAppointmentId: appt.id,
                },
              });
            })();
            }}
          >
            {t("appointments.past.details.bookAgain", "Book again")}
          </Button>

          <button
            type="button"
            className="
              flex-1 h-12 rounded-[12px]
              bg-[var(--state-critical-08)]
              text-[rgb(var(--schemes-critical))]
              text-label-large
            "
            onClick={() => setDeleteOpen(true)}
          >
            {t("appointments.details.deleteDialog.confirm", "Delete")}
          </button>
        </div>

        <AppointmentDeleteDialog
          open={deleteOpen}
          title={t(
            "appointments.details.deleteDialog.title",
            "Are you sure you want to delete this medical appointment?"
          )}
          description={t(
            "appointments.details.deleteDialog.description",
            "After deleting the medical appointment record, you will not be able to recover it."
          )}
          cancelText={t("common.no", "No")}
          confirmText={t("appointments.details.deleteDialog.confirm", "Delete")}
          onCancel={() => setDeleteOpen(false)}
          onConfirm={async () => {
            setDeleteOpen(false);
            try {
              const patientId = await getCurrentPatientId();
              const { error: deleteError } = await supabase
                .from("appointments")
                .delete()
                .eq("id", id)
                .eq("patient_id", patientId);
              if (deleteError) throw deleteError;
              window.dispatchEvent(new Event("afrimed:appointments:changed"));
            } catch {
              return;
            }
            nav("/appointments?toast=deleted", { replace: true });
          }}
        />
      </div>
      <Snackbar open={!!reviewToast} onClose={() => setReviewToast("")}>
        {reviewToast}
      </Snackbar>
    </div>
  );
}
